import { FC, useState, useEffect } from "react";
import "./login.css";
import api from "../../../services/api";
import { usePluginspace } from "../../../hooks/usePluginspace";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useAppConfig } from "../../../hooks/useAppConfig";
import { Loading } from "../../../components/Loading";
import { DecodedTokenData } from "../../../contexts/authContext";
import { parseJwt } from "../../../functions/util";
import useAuth from "../../../hooks/useAuth";
import { useConfig } from "../Auth";
import { ForgotPassword } from "../resetPassword/reset";

const JWT_SALT = process.env.REACT_APP_JWT_SALT || "ninegridCrypoToken";

function encodeCredentials(value: string): string {
  return window.btoa(unescape(encodeURIComponent(value)));
}

const Login: FC<any> = ({ configPlugin }) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const { getAppConfig } = useAppConfig();
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const { setPluginspaceData } = usePluginspace();
  const { setLoginInfo, loginInfo } = useAuth();

  const { config } = useConfig();
  const [toReset, setToReset] = useState(false);

  async function enter() {
    const config = await getAppConfig();

    if (
      config &&
      (config.template === "retail" || config.template === "food")
    ) {
      if (config.content.pdv.showLayout === "l2") {
        navigate(`/${params.pluginspace_id}/products-new`);
      } else {
        navigate(`/${params.pluginspace_id}/products`);
      }
    } else {
      navigate(`/${params.pluginspace_id}/category`);
    }
  }

  async function login() {
    if (!email.trim() || !password.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: "Verifique se preencheu email e senha e tente novamente.",
      });
      return;
    }

    setLoading(true);

    try {
      const subdomain = params.pluginspace_id
        ? atob(params.pluginspace_id)
        : atob(window.location.pathname.split("/")[1]);

      const response = await api.post(`/api/token/get-token`, {
        subdomain,
        email: encodeCredentials(email.trim()),
        pwd: encodeCredentials(password),
      });

      if (response.data.accessToken && !response.data.status) {
        localStorage.setItem(
          "loginInfo",
          JSON.stringify(response.data.accessToken),
        );
        const tkNoSalt = response.data.accessToken.split(JWT_SALT).join("");
        const tk: DecodedTokenData | null = parseJwt(tkNoSalt);

        if (tk) {
          setLoginInfo({
            accessToken: response.data.accessToken,
            expiratedAt: response.data.expiratedAt,
            identifier: tk.identifier,
            id: tk.id,
            email: tk.email,
            plugintype: tk.plugintype,
            pluginspaceId: tk.pluginspaceId,
            plugintypeId: tk.plugintypeId,
            profile: tk.profile,
          });

          setPluginspaceData(tk?.pluginspaceId);
          localStorage.removeItem("cpfReset");
          setLoading(false);
          await enter();
          return;
        }
      }

      setLoading(false);
      let message = "Usuário não encontrado.";
      if (response.data.message === "Customer is not valid.") {
        message = "Usuário não validado, favor terminar seu cadastro";
      }

      Swal.fire({
        icon: "error",
        title: "Atenção!",
        text: message,
      });
    } catch (err) {
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Atenção!",
        text: "Usuário não encontrado.",
      });
    }
  }

  const primaryColor = config?.webappcolor?.webappbutton || "rgb(7, 139, 216)";

  useEffect(() => {
    if (loginInfo && loginInfo.accessToken) {
      enter(); // Redireciona para a tela inicial se já estiver logado
    }
  }, [loginInfo]);

  return (
    <div className="login-wrapper">
      <div className="content-login">
        {loading && (
          <div className="loading-container">
            <Loading
              type="spinningBubbles"
              color="#FFF"
              width={64}
              height={64}
            />
          </div>
        )}

        <div className={`content-main ${toReset ? "itens-sobrepostos" : ""}`}>
          <div>
            {/* Botão de Voltar */}
            <button className="btn-back" onClick={() => navigate(-1)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Voltar
            </button>

            <div className="content-header">
              <h1 style={{ color: primaryColor }}>Olá!</h1>
              <span>
                Acesse sua conta para gerenciar compras e personalizar a sua
                experiência
              </span>
            </div>

            <div className="content-inputs">
              <div className="input-group">
                <div className="input-header">
                  <label>Email:</label>
                  {/* <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="9"
                    viewBox="0 0 8 9"
                    fill="none"
                  >
                    <circle cx="4" cy="4.5" r="4" fill="#37393D" />
                  </svg> */}
                </div>
                <input
                  className="myinput"
                  type="email"
                  placeholder="Seu email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              <div className="input-group">
                <div className="input-header">
                  <label>Senha:</label>
                  {/* <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="9"
                    viewBox="0 0 8 9"
                    fill="none"
                  >
                    <circle cx="4" cy="4.5" r="4" fill="#37393D" />
                  </svg> */}
                </div>
                <input
                  className="myinput"
                  type="password"
                  placeholder="*******"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>

              <div className="esqueci-senha">
                <span onClick={() => setToReset(true)}>Esqueci a senha</span>
              </div>

              <button
                className="btn-login"
                style={{ background: primaryColor }}
                onClick={() => login()}
              >
                Entrar
              </button>

              <button className="btn-secondary" onClick={() => enter()}>
                Deixar para depois
              </button>
            </div>
          </div>

          <div className="content-bottom">
            <span className="cadastro">Ainda não tem cadastro?</span>
            <span className="link">
              É simples criar sua conta no Ninegrid.{" "}
              <a
                style={{ color: primaryColor }}
                onClick={() => navigate(`/${params.pluginspace_id}/register`)}
              >
                Vamos começar?
              </a>
            </span>
          </div>
        </div>

        {/* Modal Corrigido */}
        {toReset && (
          <div className="reset-password">
            <ForgotPassword close={() => setToReset(false)} />
          </div>
        )}
      </div>
    </div>
  );
};

export { Login };
